import type { Safebox, SafeboxSlot } from "../net/messages";
import type { Inventory } from "../net/connection";
import { itemArtNode } from "./itemart";
import { iconCellsTall } from "./itemicon";
import { ItemTooltip, attachHoverTooltip, wireTooltipData } from "./tooltip";
import { elem, installChrome, makeDraggable, styleInstaller } from "./chrome";

export interface SafeboxActions {
  /** Bag slot into a storeroom cell. */
  onDeposit?: (slot: number, cell: number) => void;
  /** Storeroom cell into a bag slot. */
  onWithdraw?: (cell: number, slot: number) => void;
  /** Rearrange two cells inside the storeroom. Whole stack only. */
  onMove?: (source: number, destination: number) => void;
  /** Shut it. Load-bearing: an open box blocks a trade server-side. */
  onClose?: () => void;
  /** `/safebox_change_password <old> <new>` (`uisafebox.py:169-180`). */
  onChangePassword?: (oldPassword: string, newPassword: string) => void;
  onPick?: () => void;
}

/** `d:/ymir work/ui/game/windows/`, where the page tabs live. */
const WINDOW_ART = "/ui/textures/game/windows";
const PUBLIC_ART = "/ui/textures/public";

export const SAFEBOX_COLUMNS = 5; // safebox.cpp:17
export const SAFEBOX_ROWS = 9; // char.h:53
/** One page's cells, numbered by the server across pages. */
export const SAFEBOX_PAGE_CELLS = SAFEBOX_COLUMNS * SAFEBOX_ROWS;
const MAX_PAGE_BUTTONS = 3; // uisafebox.py:354
const SLOT_STEP = 32; // uisafebox.py:359
export const SAFEBOX_WIDTH = 176; // safeboxwindow.py:11, uisafebox.py:194
export const SAFEBOX_HEIGHT = 130 + SLOT_STEP * SAFEBOX_ROWS; // uisafebox.py:361
const PAGE_BUTTON_WIDTH = 52; // tab_button_middle_01.sub: right 501 - left 449
const PAGE_BUTTON_STEP = 52; // uisafebox.py:318, :335
const PAGE_BUTTON_BOTTOM = 85; // uisafebox.py:328
const LARGE_BUTTON_WIDTH = 88; // large_button_01.sub
const CHANGE_PASSWORD_BOTTOM = 58; // safeboxwindow.py:52
const EXIT_BOTTOM = 37; // safeboxwindow.py:67
const DRAG_SLOP_PX = 4;

/** `SAFE_TITLE` (`locale_interface.txt:280`). */
export const SAFE_TITLE = "Storeroom";
/** `SAFE_CHANGE_PASSWORD` (`locale_interface.txt:279`). */
export const SAFE_CHANGE_PASSWORD = "Change Password";
/** `CHANGE_PASSWORD_TITLE` / `_OLD` / `_NEW` / `_CONFIRM` (`locale_interface.txt:5-8`). */
export const CHANGE_PASSWORD_TITLE = "Password Change";
const CHANGE_PASSWORD_LABELS = ["Old Password", "New Password", "Confirm new Password"] as const;
/** `SAFEBOX_WRONG_PASSWORD` (`locale_game.txt:412`). */
export const SAFEBOX_WRONG_PASSWORD = "The passwords have to be identical.";
const PASSWORD_DIGITS = 6; // changepassworddialog.py:87

/** A bottom-aligned child's top: `parent height - y` (`PythonWindow.cpp:233-235`). */
const fromBottom = (y: number): number => SAFEBOX_HEIGHT - y;
/** A centred child's left: `parent width / 2 - width / 2 + x` (`PythonWindow.cpp:286-288`). */
const centred = (parent: number, width: number, x: number): number =>
  Math.floor(parent / 2) - Math.floor(width / 2) + x;

/** Left edges of the page tabs: `-int((count - 1) / 2 * 52)`, then 52 apart
 *  (`uisafebox.py:318-335`), each centred on the board. */
export function pageButtonLefts(count: number): number[] {
  const start = -Math.trunc(((count - 1) / 2) * PAGE_BUTTON_STEP);
  return Array.from({ length: count }, (_, i) =>
    centred(SAFEBOX_WIDTH, PAGE_BUTTON_WIDTH, start + i * PAGE_BUTTON_STEP),
  );
}

/** Tabs the window draws: `max(1, size / 9)` capped at three (`uisafebox.py:351-356`),
 *  where the size packet counts nine rows a page (`char.cpp:5396-5397`). */
export function pageButtonCount(pages: number): number {
  return Math.min(MAX_PAGE_BUTTONS, Math.max(1, Math.floor(pages)));
}

const STYLE_ID = "safebox-style";

const STYLE = `
/* SafeboxWindow at (100, 20) (safeboxwindow.py:6-7). */
.safebox {
  position: fixed;
  left: 100px;
  top: 20px;
  z-index: 61;
  width: ${SAFEBOX_WIDTH}px;
  height: ${SAFEBOX_HEIGHT}px;
  padding: 0;
  margin: 0;
  pointer-events: auto;
}
/* TitleBar at (8, 7), 161 wide (safeboxwindow.py:34-37). */
.safebox > .m2-titlebar { left: 8px; top: 7px; right: auto; width: 161px; }
.safebox > .m2-close { z-index: 4; }
/* wndItem at (8, 35), five by nine 32px slots (uisafebox.py:249, :359). */
.safebox-grid {
  position: absolute;
  left: 8px;
  top: 35px;
  display: grid;
  grid-template-columns: repeat(${SAFEBOX_COLUMNS}, ${SLOT_STEP}px);
  grid-template-rows: repeat(${SAFEBOX_ROWS}, ${SLOT_STEP}px);
}
.safebox-cell { position: relative; display: block; overflow: visible; }
.safebox-cell .item-icon {
  position: absolute;
  left: 0;
  top: 0;
  width: ${SLOT_STEP}px;
  height: calc(${SLOT_STEP}px * var(--safebox-item-span, 1));
  margin: 0;
  pointer-events: none;
  z-index: 1;
}
.safebox-glyph {
  position: absolute;
  left: 4px;
  top: 4px;
  width: 24px;
  height: 24px;
  pointer-events: none;
  z-index: 1;
}
.safebox-count {
  position: absolute;
  right: 1px;
  bottom: 0;
  z-index: 2;
  font: 10px/1 Tahoma, "MS Sans Serif", sans-serif;
  color: #fff6d0;
  text-shadow: 1px 1px 0 #000;
  pointer-events: none;
}
.safebox-cell-dragging { opacity: 0.4; }
.safebox-drag-ghost {
  position: fixed;
  z-index: 1000;
  width: ${SLOT_STEP}px;
  height: ${SLOT_STEP}px;
  transform: translate(-50%, -50%);
  pointer-events: none;
}
.safebox-drag-ghost .item-icon, .safebox-drag-ghost svg { width: ${SLOT_STEP}px; height: ${SLOT_STEP}px; }
/* The page tabs: tab_button_middle radios, "I", "II", "III" (uisafebox.py:319-333). */
.safebox-page {
  position: absolute;
  top: ${fromBottom(PAGE_BUTTON_BOTTOM)}px;
  width: ${PAGE_BUTTON_WIDTH}px;
  height: 19px;
  padding: 0;
  border: 0;
  background: url("${WINDOW_ART}/tab_button_middle_01.png") no-repeat left top;
  color: #e8dcc0;
  font: 12px/19px Tahoma, "Segoe UI", sans-serif;
  cursor: pointer;
  image-rendering: pixelated;
}
.safebox-page:hover { background-image: url("${WINDOW_ART}/tab_button_middle_02.png"); }
.safebox-page[aria-pressed="true"] { background-image: url("${WINDOW_ART}/tab_button_middle_03.png"); }
.safebox-change-password,
.safebox-exit { position: absolute; left: ${centred(SAFEBOX_WIDTH, LARGE_BUTTON_WIDTH, 0)}px; }
.safebox-change-password { top: ${fromBottom(CHANGE_PASSWORD_BOTTOM)}px; }
/* A button's label is a centred TextLine child, not clipped to the face
   (root/ui.py:979), so "Change Password" runs past the 88px plate evenly. */
.safebox-change-password,
.safebox-exit {
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  overflow: visible;
}
.safebox-exit { top: ${fromBottom(EXIT_BOTTOM)}px; }
/* ChangePasswordDialog, a 220x137 board (changepassworddialog.py:11-24). It and
   the popup sit on #overlay, whose pointer-events: none every clickable panel
   opts back out of (style.css:66-70). */
.safebox-password {
  pointer-events: auto;
  position: fixed;
  left: calc(50% - 110px);
  top: calc(50% - 69px);
  z-index: 63;
  width: 220px;
  height: 137px;
  padding: 0;
}
.safebox-password > .m2-titlebar { left: 8px; top: 8px; right: auto; width: 204px; }
.safebox-password-slot {
  position: absolute;
  left: 135px;
  width: 61px;
  height: 18px;
  background: url("${PUBLIC_ART}/parameter_slot_02.png") no-repeat left top;
}
.safebox-password-slot label {
  position: absolute;
  left: -110px;
  top: 3px;
  width: 108px;
  font: 12px/14px Tahoma, "Segoe UI", sans-serif;
  color: #e8dcc0;
  white-space: nowrap;
}
.safebox-password-slot input {
  position: absolute;
  left: 3px;
  top: 2px;
  width: 56px;
  height: 14px;
  padding: 0;
  border: 0;
  outline: none;
  background: transparent;
  color: #f0e6d0;
  font: 12px/14px Tahoma, "Segoe UI", sans-serif;
}
.safebox-password-ok,
.safebox-password-cancel { position: absolute; top: 103px; }
.safebox-password-ok { left: ${centred(220, 61, -35)}px; }
.safebox-password-cancel { left: ${centred(220, 61, 35)}px; }
/* PopupDialog, 280x105, the message centred at y=38 and OK at y=63 (popupdialog.py:10-55). */
.safebox-popup {
  pointer-events: auto;
  position: fixed;
  left: calc(50% - 140px);
  top: calc(50% - 52px);
  z-index: 64;
  width: 280px;
  height: 105px;
  padding: 0;
}
.safebox-popup-message {
  position: absolute;
  left: 0;
  right: 0;
  top: 31px;
  text-align: center;
  font: 12px/14px Tahoma, "Segoe UI", sans-serif;
  color: #e8dcc0;
}
.safebox-popup-ok { position: absolute; left: ${centred(280, 61, 0)}px; top: 63px; }
`;

const installStyle = styleInstaller(STYLE_ID, STYLE, installChrome);

/** The lowest empty bag slot, or -1: where a stored item lands when it is
 *  taken out without naming a bag cell. */
export function firstFreeSlot(bag: Inventory): number {
  const taken = new Set(bag.slots.map((s) => s.slot));
  for (let slot = 0; slot < bag.capacity; slot++) {
    if (!taken.has(slot)) return slot;
  }
  return -1;
}

function cellLabel(held: SafeboxSlot): string {
  return held.count > 1 ? `${held.name} x${held.count}` : held.name;
}

interface CellDrag {
  readonly source: number;
  readonly pointerId: number;
  readonly startX: number;
  readonly startY: number;
  moved: boolean;
  ghost: HTMLElement | null;
}

export class SafeboxPanel {
  private readonly root: HTMLElement;
  private readonly title: HTMLElement;
  private readonly grid: HTMLElement;
  private readonly cells: HTMLButtonElement[] = [];
  /** The stored item under each visible cell, covered cells included. */
  private readonly heldAt: (SafeboxSlot | undefined)[] = Array.from({ length: SAFEBOX_PAGE_CELLS });
  private readonly pages: HTMLElement;
  private readonly password: HTMLElement;
  private readonly passwordInputs: HTMLInputElement[] = [];
  private readonly popup: HTMLElement;

  private box: Safebox | null = null;
  private bag: Inventory | null = null;
  private page = 0;
  private drag: CellDrag | null = null;
  private justDragged = false;

  /** One panel, shared by every cell -- `tooltip.ts`'s own rule. */
  private readonly tooltip: ItemTooltip;

  constructor(
    container: HTMLElement,
    private readonly actions: SafeboxActions = {},
  ) {
    const doc = container.ownerDocument;
    installStyle(doc);
    this.tooltip = new ItemTooltip(doc);

    this.root = elem(doc, "div", "safebox m2-board");
    this.root.hidden = true;
    this.root.setAttribute("role", "dialog");
    this.root.setAttribute("aria-modal", "false");

    this.title = elem(doc, "div", "m2-titlebar safebox-title", SAFE_TITLE);
    this.title.id = "safebox-title";
    this.root.setAttribute("aria-labelledby", this.title.id);
    makeDraggable(this.root, this.title);

    const close = doc.createElement("button");
    close.type = "button";
    close.className = "m2-close safebox-close";
    close.title = "Close";
    close.setAttribute("aria-label", "Close");
    close.addEventListener("click", () => this.dismiss());

    this.grid = elem(doc, "div", "safebox-grid");
    for (let i = 0; i < SAFEBOX_PAGE_CELLS; i++) {
      const cell = doc.createElement("button");
      cell.type = "button";
      cell.className = "safebox-cell m2-slot";
      const local = i;
      attachHoverTooltip(cell, this.tooltip, () => {
        const held = this.heldAt[local];
        return held === undefined ? null : wireTooltipData(held);
      });
      cell.addEventListener("click", () => this.clickCell(local));
      cell.addEventListener("pointerdown", (event) => this.onPointerDown(event, cell, local));
      cell.addEventListener("pointermove", (event) => this.onPointerMove(event, cell));
      cell.addEventListener("pointerup", (event) => this.onPointerUp(event, cell));
      cell.addEventListener("pointercancel", (event) => this.onPointerUp(event, cell));
      this.cells.push(cell);
      this.grid.append(cell);
    }

    this.pages = elem(doc, "div", "safebox-pages");
    // `__LoadWindow` ends in `SetTableSize(3)`: one tab, pressed (uisafebox.py:281, :336).
    this.drawPages(1);

    const changePassword = doc.createElement("button");
    changePassword.type = "button";
    changePassword.className = "safebox-change-password m2-large";
    changePassword.textContent = SAFE_CHANGE_PASSWORD;
    changePassword.addEventListener("click", () => this.openChangePassword());

    const exit = doc.createElement("button");
    exit.type = "button";
    exit.className = "safebox-exit m2-large";
    exit.textContent = "Close"; // locale_interface.txt:16
    exit.addEventListener("click", () => this.dismiss());

    this.root.append(this.title, close, this.grid, this.pages, changePassword, exit);

    this.password = this.makePasswordDialog(doc);
    this.popup = elem(doc, "div", "safebox-popup m2-board");
    this.popup.hidden = true;
    const message = elem(doc, "div", "safebox-popup-message", SAFEBOX_WRONG_PASSWORD);
    const popupOk = doc.createElement("button");
    popupOk.type = "button";
    popupOk.className = "safebox-popup-ok m2-middle";
    popupOk.textContent = "OK"; // locale_interface.txt:208
    popupOk.addEventListener("click", () => this.closeMismatch());
    this.popup.append(message, popupOk);

    const onEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || !this.showing) return;
      event.preventDefault();
      event.stopPropagation();
      if (!this.password.hidden) this.closeChangePassword();
      else this.dismiss();
    };
    this.root.addEventListener("keydown", onEscape);
    doc.addEventListener("keydown", (event) => {
      if (!this.root.isConnected) return;
      onEscape(event);
    });
    container.append(this.root, this.password, this.popup);
  }

  private makePasswordDialog(doc: Document): HTMLElement {
    const dialog = elem(doc, "div", "safebox-password m2-board");
    dialog.hidden = true;
    dialog.setAttribute("role", "dialog");
    const title = elem(doc, "div", "m2-titlebar", CHANGE_PASSWORD_TITLE);
    const close = doc.createElement("button");
    close.type = "button";
    close.className = "m2-close";
    close.setAttribute("aria-label", "Close");
    close.addEventListener("click", () => this.closeChangePassword());
    dialog.append(title, close);
    // The three slots at y = 34, 59, 79 (changepassworddialog.py:62-63, :96-97, :130-131).
    [34, 59, 79].forEach((top, index) => {
      const slot = elem(doc, "div", "safebox-password-slot");
      slot.style.top = `${top}px`;
      const input = doc.createElement("input");
      input.type = "password";
      input.maxLength = PASSWORD_DIGITS;
      input.id = `safebox-password-${index}`;
      const label = doc.createElement("label");
      label.htmlFor = input.id;
      label.textContent = CHANGE_PASSWORD_LABELS[index];
      // Return moves to the next field and the last one accepts
      // (`uisafebox.py:124-129`, `OnNextFocus` at :133-142).
      input.addEventListener("keydown", (event) => {
        if (event.key !== "Enter" && event.key !== "Tab") return;
        event.preventDefault();
        if (event.key === "Enter" && index === 2) this.acceptChangePassword();
        else this.passwordInputs[(index + 1) % 3].focus();
      });
      slot.append(label, input);
      this.passwordInputs.push(input);
      dialog.append(slot);
    });
    const ok = doc.createElement("button");
    ok.type = "button";
    ok.className = "safebox-password-ok m2-middle";
    ok.textContent = "OK"; // locale_interface.txt:208
    ok.addEventListener("click", () => this.acceptChangePassword());
    const cancel = doc.createElement("button");
    cancel.type = "button";
    cancel.className = "safebox-password-cancel m2-middle";
    cancel.textContent = "Cancel"; // locale_interface.txt:4
    cancel.addEventListener("click", () => this.closeChangePassword());
    dialog.append(ok, cancel);
    return dialog;
  }

  get showing(): boolean {
    return !this.root.hidden;
  }

  get element(): HTMLElement {
    return this.root;
  }

  /** Cells paid for, as the server last stated. -1 when nothing is open. */
  get capacity(): number {
    return this.box === null ? -1 : this.box.capacity;
  }

  setBag(bag: Inventory): void {
    this.bag = bag;
  }

  /** Draw a storeroom, or take it down. `npc_id` of zero means it is not open. */
  update(box: Safebox): void {
    if (box.npc_id === 0) {
      this.close();
      return;
    }
    this.box = box;
    const count = pageButtonCount(box.pages);
    this.page = Math.min(this.page, count - 1);
    this.drawPages(count);
    this.drawCells();
    this.root.hidden = false;
  }

  private drawPages(count: number): void {
    const doc = this.root.ownerDocument;
    this.pages.replaceChildren();
    const lefts = pageButtonLefts(count);
    for (let page = 0; page < count; page++) {
      const button = doc.createElement("button");
      button.type = "button";
      button.className = "safebox-page";
      button.style.left = `${lefts[page]}px`;
      button.textContent = "I".repeat(page + 1);
      button.setAttribute("aria-pressed", String(page === this.page));
      button.addEventListener("click", () => this.selectPage(page));
      this.pages.append(button);
    }
  }

  /** `SelectPage` (`uisafebox.py:338-346`). */
  selectPage(page: number): void {
    if (this.box === null || page < 0 || page >= pageButtonCount(this.box.pages)) return;
    this.page = page;
    this.drawPages(pageButtonCount(this.box.pages));
    this.drawCells();
  }

  private drawCells(): void {
    const doc = this.root.ownerDocument;
    const box = this.box;
    const start = this.page * SAFEBOX_PAGE_CELLS;
    for (let i = 0; i < SAFEBOX_PAGE_CELLS; i++) {
      const cell = this.cells[i];
      this.heldAt[i] = undefined;
      cell.replaceChildren();
      cell.className = "safebox-cell m2-slot";
      cell.dataset.safeboxCell = String(start + i);
      cell.title = "";
      cell.removeAttribute("aria-label");
      cell.style.removeProperty("--safebox-item-span");
      if (box === null || start + i >= box.capacity) cell.classList.add("safebox-cell-locked");
    }
    if (box === null) return;
    for (const held of box.cells) {
      const local = held.slot - start;
      if (local < 0 || local >= SAFEBOX_PAGE_CELLS) continue;
      const row = Math.floor(local / SAFEBOX_COLUMNS);
      const span = Math.max(1, Math.min(iconCellsTall(held.vnum), SAFEBOX_ROWS - row));
      const label = cellLabel(held);
      for (let y = 0; y < span; y++) {
        const index = local + y * SAFEBOX_COLUMNS;
        this.heldAt[index] = held;
        this.cells[index].title = label;
        if (y > 0) this.cells[index].classList.add("safebox-covered");
      }
      const cell = this.cells[local];
      cell.classList.add("safebox-item");
      cell.setAttribute("aria-label", label);
      cell.style.setProperty("--safebox-item-span", String(span));
      cell.append(itemArtNode(held.vnum, held.kind, doc, "safebox-glyph"));
      // `RefreshSafebox` passes a count of one as zero, drawn as no number
      // (`uisafebox.py:375-378`).
      if (held.count > 1) cell.append(elem(doc, "span", "safebox-count", String(held.count)));
    }
  }

  private clickCell(local: number): void {
    if (this.justDragged) {
      this.justDragged = false;
      return;
    }
    const held = this.heldAt[local];
    if (held !== undefined) this.withdraw(held.slot);
  }

  private onPointerDown(event: PointerEvent, cell: HTMLButtonElement, local: number): void {
    if (event.button !== 0 || this.drag !== null) return;
    const held = this.heldAt[local];
    if (held === undefined) return;
    cell.setPointerCapture?.(event.pointerId);
    this.drag = {
      source: held.slot,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      moved: false,
      ghost: null,
    };
  }

  private onPointerMove(event: PointerEvent, cell: HTMLButtonElement): void {
    const drag = this.drag;
    if (drag === null || event.pointerId !== drag.pointerId) return;
    if (!drag.moved) {
      if (Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY) <= DRAG_SLOP_PX) return;
      drag.moved = true;
      this.tooltip.hide();
      cell.classList.add("safebox-cell-dragging");
      const held = this.box?.cells.find((c) => c.slot === drag.source);
      const doc = this.root.ownerDocument;
      drag.ghost = elem(doc, "div", "safebox-drag-ghost");
      if (held !== undefined) drag.ghost.append(itemArtNode(held.vnum, held.kind, doc));
      doc.body.append(drag.ghost);
    }
    if (drag.ghost !== null) {
      drag.ghost.style.left = `${event.clientX}px`;
      drag.ghost.style.top = `${event.clientY}px`;
    }
  }

  private onPointerUp(event: PointerEvent, cell: HTMLButtonElement): void {
    const drag = this.drag;
    if (drag === null || event.pointerId !== drag.pointerId) return;
    this.drag = null;
    cell.classList.remove("safebox-cell-dragging");
    drag.ghost?.remove();
    if (!drag.moved) return;
    this.justDragged = true;
    if (event.type === "pointercancel") return;
    const under = this.root.ownerDocument.elementFromPoint(event.clientX, event.clientY);
    const target =
      under instanceof Element ? under.closest<HTMLElement>("[data-safebox-cell]") : null;
    if (target === null || !this.root.contains(target)) return;
    this.move(drag.source, Number(target.dataset.safeboxCell));
  }

  /** A stored item released on an empty cell of the grid
   *  (`SelectEmptySlot` with a safebox item attached, `uisafebox.py:410-412`). */
  move(source: number, destination: number): void {
    const box = this.box;
    if (box === null || source === destination) return;
    if (!Number.isInteger(destination) || destination < 0 || destination >= box.capacity) return;
    if (this.occupiedGlobal(destination)) return;
    this.actions.onMove?.(source, destination);
  }

  /** An inventory item released on a cell: `SelectEmptySlot` checks it in
   *  there (`uisafebox.py:414-424`); an occupied cell takes nothing
   *  (`SelectItemSlot`, `uisafebox.py:437-446`). */
  depositInto(slot: number, cell: number): void {
    const box = this.box;
    if (box === null || !Number.isInteger(cell) || cell < 0 || cell >= box.capacity) return;
    if (this.occupiedGlobal(cell)) return;
    // `uiinventory.py:739`'s grab-off-the-bag instant. See `onPick`.
    this.actions.onPick?.();
    this.actions.onDeposit?.(slot, cell);
  }

  /** Whether a server-numbered cell is held, by an item's own cell or by the
   *  rows a tall item covers below it. */
  private occupiedGlobal(cell: number): boolean {
    const box = this.box;
    if (box === null) return false;
    return box.cells.some((held) => {
      const span = Math.max(1, iconCellsTall(held.vnum));
      for (let y = 0; y < span; y++) {
        if (held.slot + y * SAFEBOX_COLUMNS === cell) return true;
      }
      return false;
    });
  }

  /** Ask for a cell to come out, into the first bag slot this panel sees free:
   *  a picked-up stored item dropped on the inventory is checked out
   *  (`SendSafeboxCheckoutPacket`, `uiinventory.py:673`). */
  withdraw(cell: number): void {
    if (this.box === null || this.bag === null) return;
    const slot = firstFreeSlot(this.bag);
    if (slot < 0) return;
    // `uisafebox.py:462`'s grab-off-the-cell instant. See `onPick`.
    this.actions.onPick?.();
    this.actions.onWithdraw?.(cell, slot);
  }

  /** `ChangePasswordDialog.Open` (`uisafebox.py:151-158`). */
  openChangePassword(): void {
    for (const input of this.passwordInputs) input.value = "";
    this.password.hidden = false;
    this.passwordInputs[0].focus();
  }

  closeChangePassword(): void {
    for (const input of this.passwordInputs) input.value = "";
    this.password.hidden = true;
    this.popup.hidden = true;
  }

  /** `ChangePasswordDialog.OnAccept` (`uisafebox.py:169-180`): a mismatch raises
   *  the popup and sends nothing. */
  acceptChangePassword(): void {
    const [oldPassword, newPassword, check] = this.passwordInputs.map((input) => input.value);
    if (newPassword !== check) {
      this.popup.hidden = false;
      return;
    }
    this.actions.onChangePassword?.(oldPassword, newPassword);
    this.closeChangePassword();
  }

  /** `OnCloseMessageDialog` (`uisafebox.py:186-190`). */
  private closeMismatch(): void {
    this.passwordInputs[1].value = "";
    this.passwordInputs[2].value = "";
    this.popup.hidden = true;
    this.passwordInputs[1].focus();
  }

  /** `Close` sends `/safebox_close`; the window goes when the server says so
   *  (`uisafebox.py:389-399`). */
  dismiss(): void {
    this.actions.onClose?.();
  }

  /** Hide it without saying anything -- for a death, a walk-away, or a frame
   *  that already told us it is shut (`CommandCloseSafebox`, `uisafebox.py:392-399`). */
  close(): void {
    this.tooltip.hide();
    this.drag?.ghost?.remove();
    this.drag = null;
    this.box = null;
    this.page = 0;
    this.root.hidden = true;
    this.closeChangePassword();
    this.drawPages(1);
    this.drawCells();
  }
}
